// src/components/admin/employees/wizard/BasicDetails.js
import React, { useState, useEffect } from 'react';

const BasicDetails = ({ formData, updateFormData, theme, isEmployeeIdTaken }) => {
    const [employeeIdError, setEmployeeIdError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        updateFormData({ [name]: value });

        // Validate employee ID uniqueness when manually entered
        if (name === 'employeeNumber' && formData.numberSeries === 'Manual') {
            if (value && isEmployeeIdTaken(value)) {
                setEmployeeIdError('Employee ID already taken!');
            } else {
                setEmployeeIdError('');
            }
        }
    };

    // Validate mobile number (10 digits)
    const handleMobileChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Only digits
        if (value.length <= 10) {
            updateFormData({ mobileNumber: value });
        }
    };

    const inputClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
        theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
    }`;

    const selectClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 max-h-10 ${
        theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
    }`;

    const labelClass = `block text-sm font-medium mb-1.5 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
    }`;

    const sectionTitleClass = `text-base font-semibold mb-3 ${
        theme === 'dark' ? 'text-white' : 'text-gray-900'
    }`;

    // Auto-generate display name when first or last name changes
    useEffect(() => {
        if (formData.firstName || formData.lastName) {
            const displayName = `${formData.firstName} ${formData.lastName}`.trim();
            updateFormData({ displayName });
        }
    }, [formData.firstName, formData.lastName]);

    // Comprehensive country list
    const countries = [
        "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
        "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
        "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
        "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad",
        "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
        "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
        "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia",
        "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
        "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan",
        "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho",
        "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives",
        "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco",
        "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
        "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan",
        "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
        "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines",
        "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone",
        "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan",
        "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
        "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
        "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu",
        "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
    ];

    // Comprehensive country codes for phone numbers
    const countryCodes = [
        { code: "+93", country: "Afghanistan" },
        { code: "+355", country: "Albania" },
        { code: "+213", country: "Algeria" },
        { code: "+376", country: "Andorra" },
        { code: "+244", country: "Angola" },
        { code: "+54", country: "Argentina" },
        { code: "+374", country: "Armenia" },
        { code: "+61", country: "Australia" },
        { code: "+43", country: "Austria" },
        { code: "+994", country: "Azerbaijan" },
        { code: "+973", country: "Bahrain" },
        { code: "+880", country: "Bangladesh" },
        { code: "+375", country: "Belarus" },
        { code: "+32", country: "Belgium" },
        { code: "+501", country: "Belize" },
        { code: "+229", country: "Benin" },
        { code: "+975", country: "Bhutan" },
        { code: "+591", country: "Bolivia" },
        { code: "+387", country: "Bosnia and Herzegovina" },
        { code: "+267", country: "Botswana" },
        { code: "+55", country: "Brazil" },
        { code: "+673", country: "Brunei" },
        { code: "+359", country: "Bulgaria" },
        { code: "+226", country: "Burkina Faso" },
        { code: "+257", country: "Burundi" },
        { code: "+855", country: "Cambodia" },
        { code: "+237", country: "Cameroon" },
        { code: "+1", country: "Canada" },
        { code: "+238", country: "Cape Verde" },
        { code: "+236", country: "Central African Republic" },
        { code: "+235", country: "Chad" },
        { code: "+56", country: "Chile" },
        { code: "+86", country: "China" },
        { code: "+57", country: "Colombia" },
        { code: "+506", country: "Costa Rica" },
        { code: "+385", country: "Croatia" },
        { code: "+53", country: "Cuba" },
        { code: "+357", country: "Cyprus" },
        { code: "+420", country: "Czech Republic" },
        { code: "+45", country: "Denmark" },
        { code: "+253", country: "Djibouti" },
        { code: "+593", country: "Ecuador" },
        { code: "+20", country: "Egypt" },
        { code: "+503", country: "El Salvador" },
        { code: "+372", country: "Estonia" },
        { code: "+251", country: "Ethiopia" },
        { code: "+679", country: "Fiji" },
        { code: "+358", country: "Finland" },
        { code: "+33", country: "France" },
        { code: "+241", country: "Gabon" },
        { code: "+220", country: "Gambia" },
        { code: "+995", country: "Georgia" },
        { code: "+49", country: "Germany" },
        { code: "+233", country: "Ghana" },
        { code: "+30", country: "Greece" },
        { code: "+502", country: "Guatemala" },
        { code: "+224", country: "Guinea" },
        { code: "+592", country: "Guyana" },
        { code: "+509", country: "Haiti" },
        { code: "+504", country: "Honduras" },
        { code: "+852", country: "Hong Kong" },
        { code: "+36", country: "Hungary" },
        { code: "+354", country: "Iceland" },
        { code: "+91", country: "India" },
        { code: "+62", country: "Indonesia" },
        { code: "+98", country: "Iran" },
        { code: "+964", country: "Iraq" },
        { code: "+353", country: "Ireland" },
        { code: "+972", country: "Israel" },
        { code: "+39", country: "Italy" },
        { code: "+81", country: "Japan" },
        { code: "+962", country: "Jordan" },
        { code: "+7", country: "Kazakhstan" },
        { code: "+254", country: "Kenya" },
        { code: "+965", country: "Kuwait" },
        { code: "+996", country: "Kyrgyzstan" },
        { code: "+856", country: "Laos" },
        { code: "+371", country: "Latvia" },
        { code: "+961", country: "Lebanon" },
        { code: "+266", country: "Lesotho" },
        { code: "+231", country: "Liberia" },
        { code: "+218", country: "Libya" },
        { code: "+423", country: "Liechtenstein" },
        { code: "+370", country: "Lithuania" },
        { code: "+352", country: "Luxembourg" },
        { code: "+261", country: "Madagascar" },
        { code: "+265", country: "Malawi" },
        { code: "+60", country: "Malaysia" },
        { code: "+960", country: "Maldives" },
        { code: "+223", country: "Mali" },
        { code: "+356", country: "Malta" },
        { code: "+52", country: "Mexico" },
        { code: "+373", country: "Moldova" },
        { code: "+377", country: "Monaco" },
        { code: "+976", country: "Mongolia" },
        { code: "+382", country: "Montenegro" },
        { code: "+212", country: "Morocco" },
        { code: "+258", country: "Mozambique" },
        { code: "+95", country: "Myanmar" },
        { code: "+264", country: "Namibia" },
        { code: "+977", country: "Nepal" },
        { code: "+31", country: "Netherlands" },
        { code: "+64", country: "New Zealand" },
        { code: "+505", country: "Nicaragua" },
        { code: "+227", country: "Niger" },
        { code: "+234", country: "Nigeria" },
        { code: "+47", country: "Norway" },
        { code: "+968", country: "Oman" },
        { code: "+92", country: "Pakistan" },
        { code: "+507", country: "Panama" },
        { code: "+675", country: "Papua New Guinea" },
        { code: "+595", country: "Paraguay" },
        { code: "+51", country: "Peru" },
        { code: "+63", country: "Philippines" },
        { code: "+48", country: "Poland" },
        { code: "+351", country: "Portugal" },
        { code: "+974", country: "Qatar" },
        { code: "+40", country: "Romania" },
        { code: "+7", country: "Russia" },
        { code: "+250", country: "Rwanda" },
        { code: "+966", country: "Saudi Arabia" },
        { code: "+221", country: "Senegal" },
        { code: "+381", country: "Serbia" },
        { code: "+248", country: "Seychelles" },
        { code: "+232", country: "Sierra Leone" },
        { code: "+65", country: "Singapore" },
        { code: "+421", country: "Slovakia" },
        { code: "+386", country: "Slovenia" },
        { code: "+252", country: "Somalia" },
        { code: "+27", country: "South Africa" },
        { code: "+82", country: "South Korea" },
        { code: "+211", country: "South Sudan" },
        { code: "+34", country: "Spain" },
        { code: "+94", country: "Sri Lanka" },
        { code: "+249", country: "Sudan" },
        { code: "+597", country: "Suriname" },
        { code: "+268", country: "Eswatini" },
        { code: "+46", country: "Sweden" },
        { code: "+41", country: "Switzerland" },
        { code: "+963", country: "Syria" },
        { code: "+886", country: "Taiwan" },
        { code: "+992", country: "Tajikistan" },
        { code: "+255", country: "Tanzania" },
        { code: "+66", country: "Thailand" },
        { code: "+228", country: "Togo" },
        { code: "+676", country: "Tonga" },
        { code: "+216", country: "Tunisia" },
        { code: "+90", country: "Turkey" },
        { code: "+993", country: "Turkmenistan" },
        { code: "+256", country: "Uganda" },
        { code: "+380", country: "Ukraine" },
        { code: "+971", country: "United Arab Emirates" },
        { code: "+44", country: "United Kingdom" },
        { code: "+1", country: "United States" },
        { code: "+598", country: "Uruguay" },
        { code: "+998", country: "Uzbekistan" },
        { code: "+678", country: "Vanuatu" },
        { code: "+58", country: "Venezuela" },
        { code: "+84", country: "Vietnam" },
        { code: "+967", country: "Yemen" },
        { code: "+260", country: "Zambia" },
        { code: "+263", country: "Zimbabwe" }
    ];

    return (
        <div className="space-y-4">
            <h3 className={sectionTitleClass}>Employee Details</h3>

            {/* Work Country */}
            <div>
                <label className={labelClass}>
                    Work Country<span className="text-red-500">*</span>
                </label>
                <select
                    name="workCountry"
                    value={formData.workCountry}
                    onChange={handleChange}
                    required
                    className={selectClass}
                    size="1"
                >
                    <option value="">Select Work Country</option>
                    {countries.map((country) => (
                        <option key={country} value={country}>{country}</option>
                    ))}
                </select>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        First Name<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="First Name"
                        required
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className={labelClass}>Middle Name</label>
                    <input
                        type="text"
                        name="middleName"
                        value={formData.middleName}
                        onChange={handleChange}
                        placeholder="Middle Name"
                        className={inputClass}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        Last Name<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Last Name"
                        required
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className={labelClass}>
                        Display Name<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="displayName"
                        value={formData.displayName}
                        onChange={handleChange}
                        placeholder="Display Name"
                        required
                        className={inputClass}
                    />
                </div>
            </div>

            {/* Gender and Date of Birth */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        Gender<span className="text-red-500">*</span>
                    </label>
                    <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        required
                        className={selectClass}
                        size="1"
                    >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                </div>
                <div>
                    <label className={labelClass}>
                        Date of Birth<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                        className={inputClass}
                    />
                </div>
            </div>

            {/* Nationality */}
            <div>
                <label className={labelClass}>
                    Nationality<span className="text-red-500">*</span>
                </label>
                <select
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    required
                    className={selectClass}
                    size="1"
                >
                    <option value="">Select Nationality</option>
                    {countries.map((country) => (
                        <option key={country} value={country}>{country}</option>
                    ))}
                </select>
            </div>

            <h3 className={sectionTitleClass}>Employee Information</h3>

            {/* Number Series and Employee Number */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        Number Series<span className="text-red-500">*</span>
                    </label>
                    <select
                        name="numberSeries"
                        value={formData.numberSeries}
                        onChange={handleChange}
                        required
                        className={selectClass}
                        size="1"
                    >
                        <option value="Auto">Auto</option>
                        <option value="Manual">Manual</option>
                    </select>
                </div>
                <div>
                    <label className={labelClass}>
                        Employee Number<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="employeeNumber"
                        value={formData.employeeNumber}
                        onChange={handleChange}
                        placeholder={formData.numberSeries === 'Auto' ? '' : 'Enter Employee ID'}
                        required
                        disabled={formData.numberSeries === 'Auto'}
                        className={`${inputClass} ${formData.numberSeries === 'Auto' ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''} ${employeeIdError ? 'border-red-500' : ''}`}
                    />
                    {employeeIdError && (
                        <p className="text-red-500 text-xs mt-1">{employeeIdError}</p>
                    )}
                </div>
            </div>

            {/* Work Email */}
            <div>
                <label className={labelClass}>
                    Work Email<span className="text-red-500">*</span>
                </label>
                <input
                    type="email"
                    name="workEmail"
                    value={formData.workEmail}
                    onChange={handleChange}
                    placeholder="email@company.com"
                    required
                    className={inputClass}
                />
            </div>

            {/* Mobile Number with Country Code */}
            <div>
                <label className={labelClass}>
                    Mobile Number<span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-3">
                        <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleChange}
                            required
                            className={selectClass}
                            size="1"
                        >
                            {countryCodes.map((item) => (
                                <option key={item.code + item.country} value={item.code}>
                                    {item.code}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-9">
                        <input
                            type="tel"
                            name="mobileNumber"
                            value={formData.mobileNumber}
                            onChange={handleMobileChange}
                            placeholder="Enter 10-digit number"
                            required
                            minLength="10"
                            maxLength="10"
                            pattern="[0-9]{10}"
                            className={inputClass}
                        />
                        {formData.mobileNumber && formData.mobileNumber.length < 10 && (
                            <p className="text-xs text-orange-500 mt-1">
                                {10 - formData.mobileNumber.length} more digit(s) required
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicDetails;
